// ÜGYVEZETŐ-TÖRTÉNET — tiszta segédek (nem-komponens): sorrend, időszak-felirat, átfedés-észlelés és az
// automatikus lezárás. Külön fájl, mert MIND A KETTŐ felület használja: a szerkesztő-sidebar (bevitel +
// figyelmeztetés) és a lista „Részletek" blokkja (megjelenítés + ugyanaz a figyelmeztetés). Egy másolat
// elcsúszna — a lista mást tartana átfedésnek, mint az űrlap.
//
// AZ IDŐSZAK MINDKÉT VÉGE OPCIONÁLIS (user-döntés). Ezért minden itteni számítás a HIÁNYZÓ dátumot
// végtelennek veszi: nyitott kezdet = „mióta az eszünket tudjuk", nyitott vég = „ma is ő". Ez nem
// kényelmi egyszerűsítés — a legacyből örökölt nevekhez tényleg nincs dátum.
//
// ISO-DÁTUMOKON STRING-ÖSSZEHASONLÍTÁST használunk (`'2026-01-02' < '2026-02-01'`): a `YYYY-MM-DD` alak
// lexikografikus rendezése kronologikus, `Date`-példány nélkül — nincs időzóna-csapda, nincs allokáció.
import { type TFunction } from 'i18next';

import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';

/** Egy bejegyzés a történetben. A hiányzó dátum a wire-en `null`, az űrlapon üres string. */
export interface DirectorEntry {
  name: string;
  startsOn: string | null;
  endsOn: string | null;
}

/** Üres string → `null` (az űrlap-alakból a wire-alakba). */
function orNull(v: string | null): string | null {
  return v === null || v.trim() === '' ? null : v;
}

/** Egy bejegyzés normalizált (üres-string-mentes) alakja — minden itteni számítás ezen dolgozik. */
function normalize(d: DirectorEntry): DirectorEntry {
  return { name: d.name, startsOn: orNull(d.startsOn), endsOn: orNull(d.endsOn) };
}

/** Az `iso` nap ELŐTTI nap (`YYYY-MM-DD`). UTC-ben számol: a nap-aritmetika sosem zónázhat át. */
export function previousDay(iso: string): string {
  const t = Date.parse(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(t)) return iso;
  return new Date(t - 86_400_000).toISOString().slice(0, 10);
}

/**
 * AUTOMATIKUS LEZÁRÁS — a `changedIndex` bejegyzés kezdőnapja elé eső, még NYITOTT bejegyzéseket lezárja az
 * új kezdőnap ELŐTTI nappal (a ROADMAP A-152-ben rögzített szabály).
 *
 * MIÉRT AZ ŰRLAPON FUT ÉS NEM A SZERVEREN: így a felhasználó LÁTJA a beírt záró dátumot, és felül tudja
 * írni. Egy szerver-oldali, néma átírás ugyanezt az adatot állítaná elő, de a mentés után derülne ki — és
 * ha a szabály nem illik az esetére (pl. a két ügyvezető tényleg átfedett), nem tudna mit kezdeni vele.
 *
 * Kezdődátum nélküli bejegyzés SEMMIT nem zár le: nincs mihez képest lezárni (user-döntés — a dátum
 * opcionális marad, az automatizmus csak akkor lép, ha van mit számolni).
 */
export function applyAutoClose<T extends DirectorEntry>(rows: T[], changedIndex: number): T[] {
  const changed = rows[changedIndex];
  const start = changed ? orNull(changed.startsOn) : null;
  if (start === null) return rows;
  return rows.map((row, i) => {
    if (i === changedIndex) return row;
    const n = normalize(row);
    if (n.endsOn !== null) return row; // már lezárt — nem nyúlunk hozzá
    if (n.startsOn !== null && n.startsOn >= start) return row; // nem ELŐZŐ bejegyzés
    return { ...row, endsOn: previousDay(start) };
  });
}

/**
 * Az ÁTFEDŐ bejegyzések indexei. Két bejegyzés akkor fed át, ha az időszakuk metszete nem üres — a hiányzó
 * kezdet `-∞`, a hiányzó vég `+∞`. Így két, teljesen dátum nélküli bejegyzés IS átfedésnek számít: az
 * valóban eldönthetetlen, melyikük érvényes ma.
 *
 * A szerződés ezt NEM tiltja (user-döntés): a hiányos valóságot rögzíteni kell tudni. A felület viszont
 * jelzi — a szerkesztőben és a lista részlet-blokkjában is.
 */
export function overlappingDirectorIndexes(rows: DirectorEntry[]): Set<number> {
  const out = new Set<number>();
  const items = rows.map(normalize);
  for (let i = 0; i < items.length; i++) {
    const a = items[i]!;
    if (a.name.trim() === '') continue; // üres (most hozzáadott) sor még nem állítás
    for (let j = i + 1; j < items.length; j++) {
      const b = items[j]!;
      if (b.name.trim() === '') continue;
      const aStartsAfterBEnds = a.startsOn !== null && b.endsOn !== null && a.startsOn > b.endsOn;
      const bStartsAfterAEnds = b.startsOn !== null && a.endsOn !== null && b.startsOn > a.endsOn;
      if (aStartsAfterBEnds || bStartsAfterAEnds) continue; // diszjunkt
      out.add(i);
      out.add(j);
    }
  }
  return out;
}

/**
 * MEGJELENÍTÉSI sorrend: a legfrissebb elöl. Rendezés a kezdőnap szerint csökkenően, a dátum nélküli kezdet
 * HÁTRA (az a legrégebbi ismeretlen), azonos kezdetnél a nyitott vég előre. A TÁROLT sorrend érintetlen
 * marad (a felhasználó által felvett sorrend az övé) — ez csak a kiíratásé.
 */
/**
 * A MA érvényes ügyvezető neve (a hiányzó dátum végtelen — lásd a fájl fejlécét). Több érvényes sor esetén
 * (átfedés) a megjelenítési sorrend első eleme. Nincs ilyen → `null`. `today`: `YYYY-MM-DD`.
 */
export function currentDirectorName(rows: DirectorEntry[], today: string): string | null {
  const valid = rows.filter((r) => {
    const { startsOn, endsOn } = normalize(r);
    return (startsOn === null || startsOn <= today) && (endsOn === null || endsOn >= today);
  });
  return sortDirectorsForDisplay(valid)[0]?.name ?? null;
}

export function sortDirectorsForDisplay<T extends DirectorEntry>(rows: T[]): T[] {
  return [...rows].sort((x, y) => {
    const a = normalize(x);
    const b = normalize(y);
    if (a.startsOn !== b.startsOn) {
      if (a.startsOn === null) return 1;
      if (b.startsOn === null) return -1;
      return b.startsOn.localeCompare(a.startsOn);
    }
    if (a.endsOn === b.endsOn) return 0;
    if (a.endsOn === null) return -1;
    if (b.endsOn === null) return 1;
    return b.endsOn.localeCompare(a.endsOn);
  });
}

/** Egy `YYYY-MM-DD` nap lokalizált, rövid alakja. A DÉLI időpont a nap-eltolódás ellen véd (a lap zónája
 *  `Europe/Budapest`, a nyers `T00:00Z` egy nappal korábbra csúszna). */
function dayLabel(day: string, language: string): string {
  return formatDateTime(`${day}T12:00:00.000Z`, language, DEFAULT_TIME_ZONE, {
    dateStyle: 'medium',
  });
}

/**
 * Az időszak ember-olvasható felirata. Négy eset, mind saját i18n-kulcsból (nincs összefűzött mondat-darab):
 * `2024. jan. 1. – 2025. dec. 31.` · `2024. jan. 1. óta` · `2025. dec. 31-ig` · `ismeretlen időszak`.
 */
export function formatDirectorPeriod(entry: DirectorEntry, language: string, t: TFunction): string {
  const from = orNull(entry.startsOn);
  const to = orNull(entry.endsOn);
  if (from !== null && to !== null) {
    return t('billingProfiles.directors.periodRange', {
      from: dayLabel(from, language),
      to: dayLabel(to, language),
    });
  }
  if (from !== null)
    return t('billingProfiles.directors.periodFrom', { from: dayLabel(from, language) });
  // A magyar „-ig" RAGOT kap a dátum, ezért a formázott alak záró PONTJA lekerül: `2026. márc. 14.-ig`
  // helyett `2026. márc. 14-ig` (a rag elnyeli a sorszám-pontot). Az angol sablon elöl áll („until …"),
  // ott nincs mit levágni.
  if (to !== null) {
    return t('billingProfiles.directors.periodUntil', {
      to: dayLabel(to, language).replace(/\.$/, ''),
    });
  }
  return t('billingProfiles.directors.periodUnknown');
}
